<template>
  <b-container class="facturation mt-5">
    <h1>{{ $t("facturation.title") }}</h1>
    <section>
      <h2>{{ $t("facturation.minimumFacturable") }}</h2>
      <b-card class="default">
        <b-row>
          <b-col md="6">
            <!-- form group entreprise -->
            <b-form-group
              id="entreprise-select-group"
              :label="$t('facturation.entreprise')"
              label-for="entreprise-select"
            >
              <b-form-select
                id="entreprise-select"
                v-model="selectedEntrepriseId"
                :options="entreprises"
                @change="loadMinimumFacturable()"
                value-field="entreprise.id"
                text-field="translation.name"
              ></b-form-select>
            </b-form-group>
            <!-- form group typeTravaux -->
            <b-form-group
              id="typeTravaux-select-group"
              :label="$t('facturation.typeTravaux')"
              label-for="typeTravaux-select"
            >
              <b-form-select
                id="typeTravaux-select"
                v-model="selectedTypeTravauxId"
                :options="typeTravaux"
                @change="loadMinimumFacturable()"
                value-field="id"
                text-field="name"
              ></b-form-select>
            </b-form-group>
            <!-- form group bop models -->
            <b-form-group
              id="bopModel-select-group"
              :label="$t('facturation.bopModel')"
              label-for="bopModel-select"
            >
              <b-form-select
                id="bopModel-select"
                v-model="selectedBopModelId"
                @change="loadMinimumFacturable()"
                :options="bopModels"
                value-field="bopModel.id"
                text-field="translation.name"
              ></b-form-select>
            </b-form-group>
            <!-- form group minimum points -->
            <b-form-group
              id="minimumPoints-input-group"
              :label="$t('facturation.minimumPoints')"
              label-for="minimumPoints-input"
            >
              <b-input
                :disabled="isMinimumPointsDisabled"
                type="text"
                id="minimumPoints-input"
                v-model="minimumFacturable.minimumPoints"
              ></b-input>
            </b-form-group>
            <b-form-group
              id="isPrincipalOnly-input-group"
              :label="$t('facturation.mode')"
              label-for="isPrincipalOnly-input"
            >
              <b-form-checkbox
                class="principal-only-switch"
                id="isPrincipalOnly-input"
                switch
                :disabled="isMinimumPointsDisabled"
                v-model="IsPrincipalOnly"
              >
                {{
                  IsPrincipalOnly
                    ? $t("facturation.principalOnly")
                    : $t("facturation.allOperations")
                }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>
      <b-button variant="primary" class="float-right mt-4" @click="save()">
        {{ $t("facturation.saveMinimumFacturable") }}
      </b-button>
    </section>
  </b-container>
</template>

<script lang="ts">
import MinimumFacturable from "@/models/MinimumFacturable";
import BopModelService from "@/services/BopModelService";
import EntreprisesServices from "@/services/EntreprisesServices";
import MinimumFacturableService from "@/services/MinimumFacturableService";
import TypeTravauxService from "@/services/TypeTravauxService";
import { Guid } from "guid-typescript";
import { Component,Vue,Watch } from "vue-property-decorator";

@Component({
  components: {},
})
export default class Facturation extends Vue {
  private isMinimumPointsDisabled: boolean = true;
  private entreprises: Array<Object> = [];
  private typeTravaux: Array<Object> = [];
  private bopModels: Array<Object> = [];
  private selectedEntrepriseId: any = null;
  private selectedTypeTravauxId: any = null;
  private selectedBopModelId: any = null;
  private minimumFacturable: MinimumFacturable = new MinimumFacturable();

  private get IsPrincipalOnly(): boolean {
    return this.minimumFacturable.isPrincipalOnly;
  }

  private set IsPrincipalOnly(value: boolean) {
    this.minimumFacturable.isPrincipalOnly = value;
  }

  private async save() {
    if (!this.isMinimumPointsDisabled) {
      try {
        const response = await MinimumFacturableService.save(
          this.minimumFacturable
        );
        if (response.status === 200) {
          this.$notify({
            group: "global",
            type: "success",
            title: this.$t("facturation.saveSuccessTitle").toString(),
            text: this.$t("facturation.saveSuccess").toString(),
          });
        } else {
          this.$notify({
            group: "global",
            type: "success",
            title: this.$t("facturation.errorTitle").toString(),
            text: this.$t("facturation.uknownError").toString(),
          });
        }
      } catch {
        this.$notify({
          group: "global",
          type: "success",
          title: this.$t("facturation.errorTitle").toString(),
          text: this.$t("facturation.uknownError").toString(),
        });
      }
    }
  }
  private async loadMinimumFacturable() {
    this.isMinimumPointsDisabled =
      this.selectedEntrepriseId == null ||
      this.selectedTypeTravauxId == null ||
      this.selectedBopModelId == null;

    if (!this.isMinimumPointsDisabled) {
      let minimumFacturable: any = {};
      try {
        minimumFacturable = (
          await MinimumFacturableService.getByEntrepriseBopModelDomainApplication(
            this.selectedBopModelId,
            this.selectedEntrepriseId,
            this.selectedTypeTravauxId
          )
        ).data;
      } catch {
        minimumFacturable = {
          id: Guid.createEmpty().toString(),
          bopModelId: this.selectedBopModelId,
          entrepriseId: this.selectedEntrepriseId,
          typeTravauxId: this.selectedTypeTravauxId,
          minimumPoints: 0,
          isPrincipalOnly: false,
        };
      }
      this.minimumFacturable.copyConstructor(minimumFacturable);
    }
  }
  private async loadElements() {
    this.entreprises = (
      await EntreprisesServices.getEntreprises(this.$i18n.locale)
    ).data;
    this.typeTravaux = (
      await TypeTravauxService.listTypeTravaux(this.$i18n.locale)
    ).data;
    this.bopModels = (
      await BopModelService.getBopModels(this.$i18n.locale)
    ).data;
  }
  public created() {
    this.loadElements();
  }
  @Watch("$i18n.locale")
  private onLocaleChange() {
    this.loadElements();
  }
}
</script>
<style lang="scss" scoped>
::v-deep .principal-only-switch.custom-switch {
  padding-left: 3.2rem;
  min-height: 1.6rem;
}

::v-deep .principal-only-switch.custom-switch .custom-control-label {
  display: inline-flex;
  align-items: center;
  min-height: 1.6rem;
}

::v-deep .principal-only-switch.custom-switch .custom-control-label::before {
  width: 2.6rem;
  height: 1.4rem;
  top: 0.1rem;
  left: -3.2rem;
  border-radius: 1rem;
}

::v-deep .principal-only-switch.custom-switch .custom-control-label::after {
  width: 1.1rem;
  height: 1.1rem;
  top: 0.25rem;
  left: calc(-3.2rem + 0.15rem);
  border-radius: 50%;
}

::v-deep
  .principal-only-switch.custom-switch
  .custom-control-input:checked
  ~ .custom-control-label::after {
  transform: translateX(1.1rem);
}
</style>
